import React, { useMemo } from "react";
import PropTypes from "prop-types";
import useModalOutsideClick from "../../hooks/useModalOutsideClick";
import "./ViewEntrantsModal.css";
import { formatNumber } from "../../../../utils/helpers";

const ViewEntrantsModal = ({
  show,
  onClose,
  entrants,
  players,
  currentUsername,
  onEntryClick,
  contestStatus,
  showOnlyUserEntries,
  customTitle,
  sport
}) => {
  // Add the useModalOutsideClick hook to close when clicking outside
  useModalOutsideClick(show, onClose, "view-entrants-overlay");

  // --- Create Player Status Map --- 
  const playerStatusMap = useMemo(() => {
    const map = {};
    if (players) {
      players.forEach(player => {
        map[player.name] = { 
          finish: player.finish ?? 0,
          // Add score from sportSpecificData
          score: player.sportSpecificData?.score ?? null
        }; 
      });
    }
    return map;
  }, [players]); // Depend on players prop

  // Helper to check if any player in an entry's team is active
  const isEntryActive = (entrant) => {
    if (!entrant?.team || contestStatus !== 'Running') return true; // Default to active if not running or no team

    const isPlayerActive = (playerName) => {
      const playerStatus = playerStatusMap[playerName];
      if (sport === 'Poker') {
        // For Poker, eliminated if finish is > 0 OR -1. Active if 0 or null.
        return playerStatus?.finish === 0 || playerStatus?.finish === null;
      }
      // Default logic for other sports
      return playerStatus?.finish === 0 || playerStatus?.finish === null;
    };
    
    // Check captain
    if (isPlayerActive(entrant.team.captain)) {
      return true;
    }

    // Check flex players
    for (const playerName of entrant.team.flexPlayers) {
      if (isPlayerActive(playerName)) {
        return true; // At least one flex player is active
      }
    }

    return false; // No players active
  };

  // Calculate score for each entrant
  const calculateEntrantScore = (entrant) => {
    if (!entrant?.team) return null;
    
    let totalScore = 0;
    let playersWithScore = 0;
    
    // Calculate captain's score (double it if necessary)
    const captainData = playerStatusMap[entrant.team.captain];
    if (captainData && typeof captainData.score === 'number') {
      totalScore += captainData.score;
      playersWithScore++;
    }
    
    // Calculate flex players' scores
    for (const playerName of entrant.team.flexPlayers) {
      const playerData = playerStatusMap[playerName];
      if (playerData && typeof playerData.score === 'number') {
        totalScore += playerData.score;
        playersWithScore++;
      }
    }
    
    return playersWithScore > 0 ? totalScore : null;
  };

  // Format score for display
  const formatScore = (score) => {
    if (score === null) return '-';
    if (score === 0) return 'E';
    return score > 0 ? `+${score}` : score.toString();
  };

  // Filter and sort entrants
  const { myEntries, otherEntries } = useMemo(() => {
    if (!entrants || entrants.length === 0)
      return { myEntries: [], otherEntries: [] };

    // Helper function for natural sorting (handling numbers correctly)
    const naturalSort = (a, b) => {
      // Get display name (entryName or username)
      const nameA = (a.entryName || a.username || "").toLowerCase();
      const nameB = (b.entryName || b.username || "").toLowerCase();

      // Extract the base name and number parts for comparison
      const extractParts = (str) => {
        // Match pattern like "username[number]"
        const match = str.match(/^(.*?)(?:\[(\d+)\])?$/);
        if (!match) return { base: str, num: 0 };

        // If there's a number in brackets, extract it
        return {
          base: match[1], // The username part
          num: match[2] ? parseInt(match[2], 10) : 0, // The number in brackets, or 0 if none
        };
      };

      const partsA = extractParts(nameA);
      const partsB = extractParts(nameB);

      // First compare by base name
      if (partsA.base !== partsB.base) {
        return partsA.base.localeCompare(partsB.base);
      }

      // If base names are the same, compare by number
      return partsA.num - partsB.num;
    };

    const sorted = [...entrants].sort(naturalSort);

    const my = [];
    const others = [];
    const lowerCaseCurrentUsername = currentUsername?.toLowerCase();

    sorted.forEach((entrant) => {
      const lowerCaseEntrantUsername = entrant.username?.toLowerCase();

      if (
        lowerCaseCurrentUsername &&
        lowerCaseEntrantUsername === lowerCaseCurrentUsername
      ) {
        my.push(entrant);
      } else {
        others.push(entrant);
      }
    });
    return { myEntries: my, otherEntries: others };
  }, [entrants, currentUsername]);

  if (!show) {
    return null;
  }

  const totalEntrants = showOnlyUserEntries ? myEntries.length : (myEntries?.length || 0) + (otherEntries?.length || 0);

  return (
    <div className="view-entrants-overlay">
      <div className="view-entrants-dialog">
        <div className="view-entrants-header">
          <h3>{customTitle || (showOnlyUserEntries ? 'Your Entries' : `Current Contest Entrants (${totalEntrants})`)}</h3>
          <button className="view-entrants-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="view-entrants-content">
          {totalEntrants > 0 ? (
            <>
              {myEntries.length > 0 && (
                <div className="entrants-section">
                  <div className="your-entries-header-container">
                    <h4 className="entrants-section-header">
                      {showOnlyUserEntries ? `Your Entries (${myEntries.length})` : `Your Entries (${myEntries.length})`}
                    </h4>
                    <p className="entrants-info-message">
                      {contestStatus === 'Registering' 
                        ? "Click an entry to withdraw or view"
                        : "Click an entry to view"}
                    </p>
                  </div>
                  <div className="view-entrants-list">
                    {myEntries.map((entrant, index) => {
                      // Determine status class for the entry
                      const isActive = isEntryActive(entrant);
                      const statusClass = contestStatus === 'Running' ? (isActive ? 'player-active' : 'player-eliminated') : '';
                      const entrantScore = calculateEntrantScore(entrant);
                      const formattedScore = formatScore(entrantScore);
                      
                      return (
                        <div
                          key={entrant._id || `my-${index}`}
                          className={`view-entrant-item my-entry-item ${statusClass}`}
                          onClick={() => onEntryClick(entrant)}
                          style={{ cursor: "pointer" }}
                          title={`Salary: $${entrant.teamSalary ? formatNumber(entrant.teamSalary) : 'N/A'}`}
                        >
                          <div className="entrant-name-salary">
                            {entrant.entryName || entrant.username}
                            {typeof entrant.teamSalary === 'number' && (
                              <span className="entrant-salary">
                                (${formatNumber(entrant.teamSalary)})
                              </span>
                            )}
                          </div>
                          {contestStatus === 'Running' && (
                            <div className="entrant-score">
                              {formattedScore}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!showOnlyUserEntries && otherEntries.length > 0 && (
                <div className="entrants-section">
                  <div className="your-entries-header-container">
                    <h4 className="entrants-section-header">
                      Other Entries ({otherEntries.length})
                    </h4>
                    {contestStatus !== 'Registering' && (
                      <p className="entrants-info-message">Click an entry to view</p>
                    )}
                  </div>
                  <div className="view-entrants-list other-entries-list">
                    {otherEntries.map((entrant, index) => {
                      // Determine status class for the entry
                      const isActive = isEntryActive(entrant);
                      const statusClass = contestStatus === 'Running' ? (isActive ? 'player-active' : 'player-eliminated') : '';
                      const entrantScore = calculateEntrantScore(entrant);
                      const formattedScore = formatScore(entrantScore);
                      
                      return (
                        <div
                          key={entrant._id || `other-${index}`}
                          className={`view-entrant-item ${statusClass}`}
                          onClick={contestStatus !== 'Registering' ? () => onEntryClick(entrant) : undefined}
                          style={contestStatus !== 'Registering' ? { cursor: "pointer" } : {}}
                          title={contestStatus !== 'Registering' ? `Salary: $${entrant.teamSalary ? formatNumber(entrant.teamSalary) : 'N/A'}` : ''}
                        >
                          <div className="entrant-name-salary">
                            {entrant.entryName || entrant.username}
                            {contestStatus !== 'Registering' && typeof entrant.teamSalary === 'number' && (
                              <span className="entrant-salary">
                                (${formatNumber(entrant.teamSalary)})
                              </span>
                            )}
                          </div>
                          {contestStatus === 'Running' && (
                            <div className="entrant-score">
                              {formattedScore}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="no-entrants-message">No entries found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

ViewEntrantsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  entrants: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      username: PropTypes.string.isRequired,
      entryName: PropTypes.string,
      teamSalary: PropTypes.number,
      // Expect team object within entrant
      team: PropTypes.shape({ 
        captain: PropTypes.string, 
        flexPlayers: PropTypes.arrayOf(PropTypes.string)
      })
    })
  ).isRequired,
  players: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      finish: PropTypes.number,
      sportSpecificData: PropTypes.shape({
        score: PropTypes.number
      })
    })
  ).isRequired,
  currentUsername: PropTypes.string,
  onEntryClick: PropTypes.func.isRequired,
  contestStatus: PropTypes.string.isRequired,
  showOnlyUserEntries: PropTypes.bool,
  customTitle: PropTypes.string,
  sport: PropTypes.string.isRequired,
};

ViewEntrantsModal.defaultProps = {
  showOnlyUserEntries: false,
  customTitle: null
};

export default ViewEntrantsModal;
